import { BorderBox } from "@/components/atoms/BorderBox";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { FieldRenderer } from "@/features/question/components/organisms/FieldRenderer";
import { CheckProjectAccess } from "@/features/rbac/components/molecules/CheckProjectAccess";
import { useChangeValidStatus } from "@/features/response/hooks/useMutation/useChangeValidStatus";
// import { UserAvatar } from "@/features/user/components/atoms/user-avatar";
import { cn } from "@/lib/utils";
import { useProjectStore, useUserStore } from "@/lib/zustand/useStore";
import { formatDate } from "date-fns";
import { ArchiveRestore, Link, TicketCheck, TicketSlash, Trash2, UserStar } from "lucide-react";
import { useMemo } from "react";
import { ResponseProps } from ".";
import { HoverReveal } from "@/components/custom/HoverReveal";

export function Response({
    response,
    onRemoveOrRetrieveResponseHandler,
    responseId,
    question
}: Readonly<ResponseProps>) {
    const isResponseSelected = useMemo(() => {
        return responseId === response.id;
    }, [responseId, response.id]);
    const { project } = useProjectStore();

    const user = useUserStore.getState();
    const role = project?.project_members.find(member => member.user_id === response.user_id)?.role;
    const { mutateAsync: changeValidStatus } = useChangeValidStatus();

    const changeValidStatusHandler = async ({ response_id, is_valid, selected_user_id }: { response_id: string, is_valid: boolean | null, selected_user_id: string }) => {
        const isValid = !is_valid;
        await changeValidStatus({ response_id, is_valid: isValid, selected_user_id: selected_user_id })
            .then(() => {
                SuccessPopup("Response valid status changed successfully")
            })
            .catch((error) => {
                console.error(error);
                ErrorPopup("Failed to change response valid status");
                // Optionally, you can show a notification or alert to the user
            });
    }

    const userId = user.id;
    const isOwnResponse = response.user_id === user.id;

    return (
        <div 
            className={cn("flex gap-2", {
                "justify-end": isOwnResponse
            })}
        >
            { !isOwnResponse && (
                <div className="text-xs font-semibold bg-gradient-to-b from-white to-[#EDEDED] border border-[#EDEDED] ms-1 shadow shadow-black/25 rounded-md h-[28px] w-[28px] flex items-center justify-center">
                    {(response?.user?.first_name?.[0] || "") + "" + (response?.user?.last_name?.[0] || "")}
                </div>
            )}

            <div
                className={cn("flex flex-col w-[80%] me-2")}
                ref={ref => {
                    if (ref && isResponseSelected && ref.offsetTop > 200) {
                        ref.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    }
                    return ref
                }}
                id={response.id}
            >
                <div className="flex items-center pb-1 text-xs font-semibold gap-1">
                    { !isOwnResponse && (
                        <>
                            {(role?.label === "Project manager") && <UserStar size="14" className="text-gray-500"/>}

                            <span className="leading-none">
                                {response.user?.first_name} {response.user?.last_name}
                            </span>
                        </>
                    )}

                    <p className="text-[10px] font-normal text-neutral-500 leading-none place-self-end">{formatDate(response.created_at!, 'PPPPpp')}</p>
                </div>

                <div className="flex gap-2">

                    <div className="flex-1">
                        <div
                            className={cn("border rounded-md transition-colors w-fullflex items-start gap-1 text-black relative", {})}
                        >
                            <HoverReveal
                                side={ isOwnResponse ? "left" : "right"}
                                panelContent={
                                    <div className="flex gap-1 justify-end ring-0 shadow-none p-0">
                                        <TooltipButton
                                            variant="outline"
                                            size="action"
                                            tooltipContent="Copy link to the response"
                                            onClick={() => {
                                                const url = new URL(globalThis.location.href)
                                                url.searchParams.set('questionId', question.id)
                                                url.searchParams.set('responseId', response.id)
                                                navigator.clipboard.writeText(url.toString())
                                            }}
                                        >
                                            <Link size={14} />
                                        </TooltipButton>

                                        <CheckProjectAccess response_update>
                                            <TooltipButton
                                                tooltipContent={response.archived ? "Restore response" : "Archive response"}
                                                size="action"
                                                variant={response.archived ? "accent" : "destructive"}
                                                onClick={() => onRemoveOrRetrieveResponseHandler(response, !!response.archived)}
                                            >
                                                {response.archived ? (
                                                    <ArchiveRestore size={16} />
                                                ) : (
                                                    <Trash2 size={16} />
                                                )}
                                            </TooltipButton>
                                        </CheckProjectAccess>

                                        <CheckProjectAccess response_validate>
                                            {response.field_value && (
                                                <TooltipButton
                                                    tooltipContent={response.valid ? "Mark as Invalid" : "Mark as Valid"}
                                                    onClick={() => {
                                                        changeValidStatusHandler({ response_id: response.id, is_valid: response.valid, selected_user_id: response.user_id })
                                                    }}
                                                    size="action"
                                                    variant="outline"
                                                >
                                                    {response.valid ? (
                                                        <TicketCheck size={16} />
                                                    ) : (
                                                        <TicketSlash size={16} />
                                                    )}
                                                </TooltipButton>
                                            )}
                                        </CheckProjectAccess>
                                    </div>
                                }
                            >
                                
                                <div className="flex flex-col w-full gap-2 ">

                                    { response.content && (
                                        <div className="flex flex-col flex-1 gap-2 text-sm p-2">
                                            {/* Comment */}
                                            <p>{response.content}</p>
                                        </div>
                                    )}

                                    {response.field_value && (
                                        <div className="flex flex-col gap-2 min-w-[250px] text-sm">
                                            {/* Value */}
                                            <BorderBox
                                                className={cn("box-border p-3 text-black justify-start border-0 rounded-t-none ", 
                                                    (userId === response?.user?.id) ? "bg-[#FFF1E6]" : "bg-[#E9F2FF]",
                                                    response.valid && "bg-green-200"
                                                )}
                                                icon={false}
                                            >
                                                <FieldRenderer
                                                    question={question}
                                                    response={response}
                                                    disabled
                                                    crmSystem={project?.crm_system || "collaborate"}
                                                />
                                            </BorderBox>
                                        </div>
                                    )}
                                </div>

                            </HoverReveal>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

